import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../axiosConfig';

// =============================================
// ASYNC THUNKS
// =============================================

// Fetch all roles for the store
export const fetchRoles = createAsyncThunk(
  'roles/fetchRoles',
  async ({ includePermissions = false, includeMembers = false } = {}) => {
    const params = new URLSearchParams();
    if (includePermissions) params.append('includePermissions', 'true');
    if (includeMembers) params.append('includeMembers', 'true');
    const res = await axios.get(`/roles?${params.toString()}`);
    return res.data;
  }
);

// Fetch a single role by ID
export const fetchRoleById = createAsyncThunk(
  'roles/fetchRoleById',
  async (roleId) => {
    const res = await axios.get(`/roles/${roleId}`);
    return res.data;
  }
);

// Create a new custom role
export const createRole = createAsyncThunk(
  'roles/createRole',
  async (roleData, { rejectWithValue }) => {
    try {
      const res = await axios.post('/roles', roleData);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to create role' });
    }
  }
);

// Update a role
export const updateRole = createAsyncThunk(
  'roles/updateRole',
  async ({ roleId, roleData }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`/roles/${roleId}`, roleData);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to update role' });
    }
  }
);

// Delete a role
export const deleteRole = createAsyncThunk(
  'roles/deleteRole',
  async ({ roleId, fallbackRoleId }, { rejectWithValue }) => {
    try {
      const params = fallbackRoleId ? `?fallbackRoleId=${fallbackRoleId}` : '';
      const res = await axios.delete(`/roles/${roleId}${params}`);
      return { ...res.data, deletedRoleId: roleId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to delete role' });
    }
  }
);

// Update role permissions
export const updateRolePermissions = createAsyncThunk(
  'roles/updateRolePermissions',
  async ({ roleId, permissionIds }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`/roles/${roleId}/permissions`, { permissionIds });
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to update role permissions' });
    }
  }
);

// Clone a role
export const cloneRole = createAsyncThunk(
  'roles/cloneRole',
  async ({ roleId, newName }, { rejectWithValue }) => {
    try {
      const res = await axios.post(`/roles/${roleId}/clone`, { name: newName });
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to clone role' });
    }
  }
);

// Set default role
export const setDefaultRole = createAsyncThunk(
  'roles/setDefaultRole',
  async (roleId, { rejectWithValue }) => {
    try {
      const res = await axios.put(`/roles/${roleId}/default`);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to set default role' });
    }
  }
);

// Initialize default roles for store
export const initializeDefaultRoles = createAsyncThunk(
  'roles/initializeDefaultRoles',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.post('/roles/initialize');
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to initialize roles' });
    }
  }
);

// =============================================
// SLICE
// =============================================

const rolesSlice = createSlice({
  name: 'roles',
  initialState: {
    roles: [],
    selectedRole: null,
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    createStatus: 'idle',
    updateStatus: 'idle',
    deleteStatus: 'idle'
  },
  reducers: {
    clearRoleError: (state) => {
      state.error = null;
    },
    clearSelectedRole: (state) => {
      state.selectedRole = null;
    },
    setSelectedRole: (state, action) => {
      state.selectedRole = action.payload;
    },
    resetStatuses: (state) => {
      state.createStatus = 'idle';
      state.updateStatus = 'idle';
      state.deleteStatus = 'idle';
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch roles
      .addCase(fetchRoles.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.roles = action.payload.roles || [];
        state.error = null;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Fetch role by ID
      .addCase(fetchRoleById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchRoleById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.selectedRole = action.payload.role;
        state.error = null;
      })
      .addCase(fetchRoleById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Create role
      .addCase(createRole.pending, (state) => {
        state.createStatus = 'loading';
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.createStatus = 'succeeded';
        state.roles.push(action.payload.role);
        state.error = null;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.createStatus = 'failed';
        state.error = action.payload?.error || action.error.message;
      })

      // Update role
      .addCase(updateRole.pending, (state) => {
        state.updateStatus = 'loading';
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        const index = state.roles.findIndex(r => r.id === action.payload.role.id);
        if (index !== -1) {
          state.roles[index] = action.payload.role;
        }
        if (state.selectedRole?.id === action.payload.role.id) {
          state.selectedRole = action.payload.role;
        }
        state.error = null;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.payload?.error || action.error.message;
      })

      // Delete role
      .addCase(deleteRole.pending, (state) => {
        state.deleteStatus = 'loading';
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.roles = state.roles.filter(r => r.id !== action.payload.deletedRoleId);
        if (state.selectedRole?.id === action.payload.deletedRoleId) {
          state.selectedRole = null;
        }
        state.error = null;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.error = action.payload?.error || action.error.message;
      })

      // Update role permissions
      .addCase(updateRolePermissions.pending, (state) => {
        state.updateStatus = 'loading';
      })
      .addCase(updateRolePermissions.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        const index = state.roles.findIndex(r => r.id === action.payload.role.id);
        if (index !== -1) {
          state.roles[index] = action.payload.role;
        }
        if (state.selectedRole?.id === action.payload.role.id) {
          state.selectedRole = action.payload.role;
        }
        state.error = null;
      })
      .addCase(updateRolePermissions.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.payload?.error || action.error.message;
      })

      // Clone role
      .addCase(cloneRole.fulfilled, (state, action) => {
        state.roles.push(action.payload.role);
        state.error = null;
      })
      .addCase(cloneRole.rejected, (state, action) => {
        state.error = action.payload?.error || action.error.message;
      })

      // Set default role
      .addCase(setDefaultRole.fulfilled, (state, action) => {
        // Update isDefault flag for all roles
        state.roles = state.roles.map(role => ({
          ...role,
          isDefault: role.id === action.payload.role.id
        }));
        state.error = null;
      })

      // Initialize default roles
      .addCase(initializeDefaultRoles.fulfilled, (state, action) => {
        state.roles = action.payload.roles || [];
        state.error = null;
      });
  }
});

// =============================================
// SELECTORS
// =============================================

export const selectAllRoles = (state) => state.roles?.roles || [];
export const selectSelectedRole = (state) => state.roles?.selectedRole;
export const selectRolesStatus = (state) => state.roles?.status || 'idle';
export const selectRolesError = (state) => state.roles?.error;
export const selectSystemRoles = (state) => (state.roles?.roles || []).filter(r => r.isSystem);
export const selectCustomRoles = (state) => (state.roles?.roles || []).filter(r => !r.isSystem);
export const selectDefaultRole = (state) => (state.roles?.roles || []).find(r => r.isDefault);

export const { clearRoleError, clearSelectedRole, setSelectedRole, resetStatuses } = rolesSlice.actions;

export default rolesSlice.reducer;
